import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";
import ffmpegCore from "@ffmpeg/core?url";

/**
 * Strip the audio track out of a video, in the browser, before uploading.
 *
 * This is what makes large videos cheap: Whisper only ever needs the audio, so
 * there is no reason to ship a 200 MB video to storage and then down to the GPU.
 * A 10-minute ad becomes ~5 MB of mono MP3 — which also keeps every upload well
 * under Supabase's default 50 MB object limit, so no plan/limit change is needed.
 *
 * 16 kHz mono is exactly Whisper's input format, so downsampling here costs
 * nothing in transcript quality — it's what the model would do anyway.
 *
 * Uses the single-threaded @ffmpeg/core, which does NOT require SharedArrayBuffer
 * (and therefore no COOP/COEP headers, which would break cross-origin images).
 */

let ffmpegPromise = null;

const getFfmpeg = async () => {
  if (!ffmpegPromise) {
    const ffmpeg = new FFmpeg();
    ffmpegPromise = (async () => {
      await ffmpeg.load({ coreURL: ffmpegCore });
      return ffmpeg;
    })();
  }
  return ffmpegPromise;
};

const safeUnlink = async (ffmpeg, name) => {
  try {
    await ffmpeg.deleteFile(name);
  } catch {
    // ignore
  }
};

/**
 * @returns {Promise<{ blob: Blob, mimeType: string, ext: string }>}
 * @throws  if the codec is unsupported or the file is too large for the WASM
 *          heap — the caller should fall back to uploading the video itself.
 */
export const extractAudio = async (file, { onProgress } = {}) => {
  const ffmpeg = await getFfmpeg();

  const inputName = `in-${Date.now()}`;
  const outputName = `out-${Date.now()}.mp3`;

  const handleProgress = ({ progress }) => {
    if (typeof onProgress === "function" && Number.isFinite(progress)) {
      onProgress(Math.max(0, Math.min(1, progress)));
    }
  };

  try {
    ffmpeg.on("progress", handleProgress);

    await ffmpeg.writeFile(inputName, await fetchFile(file));

    await ffmpeg.exec([
      "-i", inputName,
      "-vn",              // drop the video stream entirely — this is the win
      "-ac", "1",         // mono
      "-ar", "16000",     // 16 kHz, Whisper's native rate
      "-b:a", "64k",      // plenty for speech
      "-f", "mp3",
      outputName,
    ]);

    const data = await ffmpeg.readFile(outputName);
    if (!data || !data.length) {
      throw new Error("No audio track found in that video.");
    }

    return {
      blob: new Blob([data.buffer], { type: "audio/mpeg" }),
      mimeType: "audio/mpeg",
      ext: "mp3",
    };
  } finally {
    ffmpeg.off?.("progress", handleProgress);
    await safeUnlink(ffmpeg, inputName);
    await safeUnlink(ffmpeg, outputName);
  }
};
